<template>
  <MMPopupDefault class="sort-feedback-modal" @close="$emit('close')">
    <template #header>
      <MMText font="Title L/Semibold" class="header-title"> Сначала показывать </MMText>
    </template>

    <div class="modal-content">
      <div class="sort-options">
        <div
          v-for="option in sortOptions"
          :key="option.value"
          class="sort-option"
          @click="selectSort(option.value)"
        >
          <div class="option-text">
            {{ option.title }}
          </div>
          <div class="radio-button">
            <input
              type="radio"
              :value="option.value"
              :checked="selectedSort === option.value"
              @change="selectSort(option.value)"
              class="radio-input"
            />
            <div class="radio-custom"></div>
          </div>
        </div>
      </div>

      <div class="footer">
        <div v-if="showPhotoSwitcher" class="photo-toggle">
          <span class="toggle-label">Только с фото</span>
          <label class="toggle-switch">
            <input
              type="checkbox"
              :checked="onlyPhotos"
              @change="toggleOnlyPhotos"
              class="toggle-input"
            />
            <span class="toggle-slider"></span>
          </label>
        </div>

        <button @click="applySort" class="apply-button">Показать</button>
      </div>
    </div>
  </MMPopupDefault>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { MMPopupDefault, MMText } from '@mm-frontend/mm-web-ui-kit';
import { FeedbackSortType, FeedbackFilterType } from '@/shared/apollo-api/__generated__/types';
import { useFeedback } from '../../business/useFeedback';

interface IPopupData {
  currentSort: FeedbackSortType;
  currentFilters: FeedbackFilterType[];
}

const props = defineProps<{ onShowData: IPopupData }>();

const emit = defineEmits<{
  close: (data?: { sort: FeedbackSortType; filters: FeedbackFilterType[] }) => void;
}>();

const { feedback } = useFeedback();

// Опции сортировки
const sortOptions = [
  { value: FeedbackSortType.Relevance, title: 'Популярные' },
  { value: FeedbackSortType.DateDesc, title: 'Новые' },
  { value: FeedbackSortType.RatingDesc, title: 'С высокой оценкой' },
  { value: FeedbackSortType.RatingAsc, title: 'С низкой оценкой' },
];

// Состояние
const selectedSort = ref<FeedbackSortType>(props.onShowData.currentSort);
const onlyPhotos = ref<boolean>(
  props.onShowData.currentFilters.includes(FeedbackFilterType.Photos)
);
const showPhotoSwitcher = ref<boolean>(false);

// Проверяем, есть ли отзывы с фото
onMounted(() => {
  const hasPhotos = feedback.value.some((feed) => feed.photos && feed.photos.length > 0);
  showPhotoSwitcher.value = hasPhotos;
});

const selectSort = (sort: FeedbackSortType) => {
  selectedSort.value = sort;
};

const toggleOnlyPhotos = (event: Event) => {
  const target = event.target as HTMLInputElement;
  onlyPhotos.value = target.checked;
};

const applySort = () => {
  const filters = onlyPhotos.value ? [FeedbackFilterType.Photos] : [];
  // Передаем данные при закрытии модалки
  emit('close', { sort: selectedSort.value, filters });
};
</script>

<style scoped lang="stylus">
.sort-feedback-modal
  width 100%
  max-width 560px
  max-height 80vh

  +Media(Mobile)
    max-width calc(100vw - 32px)
    max-height calc(100vh - 32px)

.header-title
  text-align center

.modal-content
  padding 0

.sort-options
  display flex
  flex-direction column

.sort-option
  display flex
  justify-content space-between
  align-items center
  padding 20px
  border-bottom 1px solid var(--color-border-secondary)
  cursor pointer
  transition background-color 0.2s ease

  &:last-child
    border-bottom none

  &:hover
    background-color var(--color-background-secondary)

.option-text
  font-size 16px
  font-weight 400
  line-height 20px
  color var(--text-primary)

.radio-button
  position relative
  display flex
  align-items center

.radio-input
  position absolute
  opacity 0
  cursor pointer

.radio-custom
  width 20px
  height 20px
  border 2px solid var(--color-border-primary)
  border-radius 50%
  background-color transparent
  transition all 0.2s ease

.radio-input:checked + .radio-custom
  border-color var(--color-primary)
  background-color var(--color-primary)

.radio-input:checked + .radio-custom::after
  content ''
  position absolute
  top 50%
  left 50%
  transform translate(-50%, -50%)
  width 8px
  height 8px
  border-radius 50%
  background-color white

.footer
  padding 20px
  display flex
  flex-direction column
  gap 20px

.photo-toggle
  display flex
  align-items center
  justify-content space-between
  background-color var(--color-background-secondary)
  border-radius 20px
  padding 10px 15px

.toggle-label
  font-size 14px
  font-weight 400
  color var(--text-primary)

.toggle-switch
  position relative
  display inline-block
  width 44px
  height 24px

.toggle-input
  opacity 0
  width 0
  height 0

.toggle-slider
  position absolute
  cursor pointer
  top 0
  left 0
  right 0
  bottom 0
  background-color var(--color-border-primary)
  transition 0.2s
  border-radius 24px

  &:before
    position absolute
    content ""
    height 18px
    width 18px
    left 3px
    bottom 3px
    background-color white
    transition 0.2s
    border-radius 50%

.toggle-input:checked + .toggle-slider
  background-color var(--color-primary)

.toggle-input:checked + .toggle-slider:before
  transform translateX(20px)

.apply-button
  width 100%
  padding 20px 0
  background-color var(--color-primary)
  color white
  border none
  border-radius 12px
  font-size 16px
  font-weight 500
  cursor pointer
  transition background-color 0.2s ease

  &:hover
    background-color var(--color-primary-dark)

  &:active
    background-color var(--color-primary-darker)
</style>
